import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Close from "@material-ui/icons/Close";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: theme.palette.background.paper,
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    marginLeft: 8,
    marginRight: 8,
    fontSize: 14,
  },
  navButtons: {
    display: "flex",
    alignItems: "center",
    "& .MuiIconButton-root": {
      padding: 6,
    },
  },
  count: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginRight: 8,
    minWidth: 60,
    textAlign: "center",
  },
  noResults: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginRight: 8,
    whiteSpace: "nowrap",
  },
}));

const ChatSearchBar = ({ ticketId, onGoToMessage, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const selectedQueueIds = useMemo(() => (user?.queues || []).map((q) => q.id), [user?.queues]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(
    async (q) => {
      if (!q || !q.trim() || !ticketId) {
        setResults([]);
        setCurrentIndex(-1);
        setSearched(false);
        return;
      }
      setLoading(true);
      setSearched(false);
      try {
        const { data } = await api.get("/messages/search", {
          params: {
            q: q.trim(),
            ticketId,
            page: 1,
            limit: 100,
            queueIds: JSON.stringify(selectedQueueIds || []),
          },
        });
        const msgs = data.messages || [];
        setResults(msgs);
        setCurrentIndex(msgs.length > 0 ? 0 : -1);
        setSearched(true);
        if (msgs.length > 0) {
          onGoToMessage(msgs[0].id);
        }
      } catch (err) {
        toastError(err);
        setResults([]);
        setSearched(true);
      } finally {
        setLoading(false);
      }
    },
    [ticketId, selectedQueueIds, onGoToMessage]
  );

  useEffect(() => {
    if (!query.trim()) {
      setResults((prev) => (prev.length ? [] : prev));
      setCurrentIndex((prev) => (prev !== -1 ? -1 : prev));
      setSearched(false);
      return;
    }
    const t = setTimeout(() => search(query), 400);
    return () => clearTimeout(t);
  }, [query, search]);

  const handlePrev = () => {
    if (results.length === 0) return;
    const idx = currentIndex <= 0 ? results.length - 1 : currentIndex - 1;
    setCurrentIndex(idx);
    onGoToMessage(results[idx].id);
  };

  const handleNext = () => {
    if (results.length === 0) return;
    const idx = currentIndex >= results.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(idx);
    onGoToMessage(results[idx].id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) {
        handlePrev();
      } else {
        handleNext();
      }
    }
  };

  return (
    <Paper square elevation={0} className={classes.root}>
      <SearchIcon color="action" />
      <InputBase
        className={classes.input}
        placeholder="Buscar en el chat..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
      />
      {loading && (
        <span className={classes.noResults}>Buscando...</span>
      )}
      {!loading && results.length > 0 && (
        <>
          <span className={classes.count}>
            {currentIndex + 1} / {results.length}
          </span>
          <div className={classes.navButtons}>
            <IconButton size="small" onClick={handlePrev} title="Anterior (Shift+Enter)">
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleNext} title="Siguiente (Enter)">
              <ArrowDownward fontSize="small" />
            </IconButton>
          </div>
        </>
      )}
      {!loading && searched && results.length === 0 && query.trim() && (
        <span className={classes.noResults}>Sin resultados</span>
      )}
      <IconButton size="small" onClick={onClose} title="Cerrar (Esc)">
        <Close fontSize="small" />
      </IconButton>
    </Paper>
  );
};

export default ChatSearchBar;
